import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { DateField } from '../../../../components/inputs/DateField';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { focusValidationError } from '../../../../lib/validationFocus';
import { budgetAccountCodesApi } from '../budgetCatalogsApi';
import { ppasApi } from '../ppasApi';
import { PPA_EXPENSE_CLASS_LABEL, type PpaLineItemDto } from '../ppaTypes';

/**
 * Corrects ONE budgeted line.
 *
 * **Not `<PpaLineItemsModal>` with one row.** That one ADDS a batch under a class the reader
 * opened a folder for, which is what makes an inline-add grid the right shape; this corrects
 * a row that already exists and is addressed by its own id. Two endpoints, two jobs — and an
 * inline grid holding one locked row would be a table pretending to be a form.
 *
 * **The CLASS is not correctable, and the form says so.** It is the PPA's fund source
 * (06 Oct 2026), not the line's: it decides which ceiling the line is counted against, and a
 * line under another class belongs on a PPA filed under that class.
 *
 * **A reviewer's ADJUSTMENT is stated and never edited here.** The server keeps it across
 * this write — a typo fix must not be a way to undo a verdict — so the form shows what it
 * is rather than leaving the reader to wonder whether their correction cleared it.
 *
 * **NO Autogenerate, and the exemption is `<ResetUserPasswordModal>`'s.** Every required
 * field arrives filled from the row the reader opened, so the contract — the primary action
 * enables on the first try — is already met and the button would have nothing to do. It is
 * also the one form where generating is actively wrong: this is a CORRECTION, so overwriting
 * the values would destroy the thing the reader came to read.
 */
export function PpaLineItemModal({
  open,
  ppaId,
  expenseClass,
  line,
  onClose,
  onSaved,
}: {
  open: boolean;
  ppaId: string;
  /** The PPA's fund source — the class the line is filed under. Stated, never sent. */
  expenseClass: number | null;
  line: PpaLineItemDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [item, setItem] = useState(line.item);
  const [budgetAccountCodeId, setBudgetAccountCodeId] = useState(line.budgetAccountCodeId);
  const [startDate, setStartDate] = useState(line.startDate ?? '');
  const [endDate, setEndDate] = useState(line.endDate ?? '');
  const [amount, setAmount] = useState(String(line.amount));

  const [accounts, setAccounts] = useState<SelectOption[]>([]);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    // Fails SOFT — the account code is optional, so an empty picker blocks nothing.
    budgetAccountCodesApi
      .options(controller.signal)
      .then((options) => {
        if (controller.signal.aborted) return;

        setAccounts(
          options.map((a) => ({
            value: a.id,
            label: a.code,
            sublabel: a.description ?? undefined,
          })),
        );
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, []);

  /*
    A code the catalog has since DELETED is injected as an option, so the field states what
    the record says rather than falling back to its placeholder. Putting back what was
    recorded must not read as nothing having been chosen — the shape every deleted catalog
    row takes here.
  */
  const options =
    line.budgetAccountCodeId && !accounts.some((a) => a.value === line.budgetAccountCodeId)
      ? [
          {
            value: line.budgetAccountCodeId,
            label: line.accountCode ?? 'Recorded account',
            sublabel: 'Deleted from the chart of accounts',
          },
          ...accounts,
        ]
      : accounts;

  const parsedAmount = Number.parseFloat(amount.replace(/[^0-9.-]/g, ''));
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount >= 0;

  const canSave = item.trim().length > 0 && amountValid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await ppasApi.updateLineItem(ppaId, line.id, {
        item: item.trim(),
        budgetAccountCodeId,
        startDate: startDate || null,
        endDate: endDate || null,
        amount: parsedAmount,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this line.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'ppa-line' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Correct a budgeted line"
      size="2xl"
      id="ppa-line-modal"
      confirm={{
        id: 'ppa-line-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="ppa-line-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="ppa-line-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          WHAT CANNOT BE CORRECTED HERE, stated rather than offered as a locked control. The
          adjustment is the reviewer's and survives this write; the class is a delete and an
          add. A picker locked to one value would be a control that collects nothing.
        */}
        <PreviewTable
          variant="table-display"
          rows={[
            {
              label: 'Expense class',
              value: expenseClass === null ? null : (PPA_EXPENSE_CLASS_LABEL[expenseClass] ?? null),
            },
            ...(line.adjustedAmount === null
              ? []
              : [
                  {
                    label: 'Adjusted by review',
                    value: formatMoney(line.adjustedAmount),
                    mono: true,
                  },
                ]),
          ]}
        />

        <TextField
          id="ppa-line-item"
          label="Item"
          required
          description="What this line pays for."
          value={item}
          onChange={(e) => setItem(e.target.value)}
          error={error?.fieldError('item')}
          disabled={saving}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            id="ppa-line-budget-account-code-id"
            label="Account code"
            description="The chart-of-accounts code this line is charged to."
            value={budgetAccountCodeId}
            onChange={setBudgetAccountCodeId}
            options={options}
            disabled={saving}
            error={error?.fieldError('budgetAccountCodeId')}
          />

          <TextField
            id="ppa-line-amount"
            label="Amount"
            required
            description="What this line is asking for. Zero is a real answer; a negative is not."
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={
              amountValid
                ? error?.fieldError('amount')
                : 'Enter an amount of zero or more.'
            }
            disabled={saving}
          />

          {/*
            NEITHER date is capped at today — a budget is written for a year that has not
            started, which is the opposite of every past-date field in the 201 file.
          */}
          <DateField
            id="ppa-line-start-date"
            label="Start"
            description="When this line's spending begins. Optional."
            value={startDate}
            onChange={setStartDate}
            error={error?.fieldError('startDate')}
            disabled={saving}
          />

          <DateField
            id="ppa-line-end-date"
            label="End"
            description="When it finishes. Optional, and on or after the start."
            value={endDate}
            onChange={setEndDate}
            error={error?.fieldError('endDate')}
            disabled={saving}
          />
        </div>
      </div>
    </Modal>
  );
}
